import { ApolloProvider } from '@apollo/client/react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { fr } from 'date-fns/locale';
import { setDefaultOptions } from 'date-fns';
import React, { type FC, Suspense, useEffect } from 'react';
import TagManager from 'react-gtm-module';
import {
  Route,
  RouterProvider,
  Routes,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import parse from 'url-parse';
import './App.css';
import { initCustomerIO } from './common/Customer.io';
import { load } from './common/Segment.io/analytics';
import Impersonation from './Components/Authentication/Impersonation';
import ContextProvider from './Components/Context';
import GlobalSnackBar from './Components/CustomComponents/GlobalSnackBar';
import DigidomCgv from './Components/DigidomCgv';
import ChunkErrorBoundary from './Components/ChunkErrorBoundary';
import ErrorBoundary, { RouteErrorBoundary } from './Components/ErrorBoundary';
import MainLoadingDigidom from './Components/Loading/MainLoadingDigidom';
import apolloClient from './config/apolloClient';
import config from './config/config';
import { ERoutePrincipal } from './Interface/Dashboard/CategoryInterface';
import RouteTracker from './Services/RouteTracker';
import { themeBV } from './Theme/MyTheme';
import lazyWithRetry, {
  CHUNK_REFRESH_KEY,
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from './Utils/lazyWithRetry';
import { analytics } from './Services/analytics';

const tagManagerArgs = {
  gtmId: config.googleTagManager || '',
  dataLayerName: 'ga4Datalayer',
};

TagManager.initialize(tagManagerArgs);

const Home = lazyWithRetry(() => import('./Components/Home'));
const Login = lazyWithRetry(() => import('./Components/Authentication/Login'));
const ForgotPassword = lazyWithRetry(() => import('./Components/Authentication/ForgotPassword'));
const LogOut = lazyWithRetry(() => import('./Components/Authentication/LogOut'));

const GoCardLessValidation = lazyWithRetry(
  () => import('./Components/Authentication/GoCardLessValidation'),
);
const ViewDocument = lazyWithRetry(() => import('./Components/ViewDocument'));

setDefaultOptions({ locale: fr });

export { analytics };

declare global {
  interface Window {
    $crisp: any;
    CRISP_WEBSITE_ID: string;
    __gCrWeb: any;
  }
}

const App: FC = () => {
  useEffect(() => {
    // crisp (le global est déjà initialisé dans index.html pour éviter une race avec GTM)
    window.$crisp = window.$crisp || [];
    window.$crisp.push(['safe', true]);
    window.CRISP_WEBSITE_ID = config.crispWebSiteId!;
    (() => {
      const d = document;
      const s = d.createElement('script');
      s.src = 'https://client.crisp.chat/l.js';
      s.async = true;
      d.getElementsByTagName('head')[0].appendChild(s);
    })();

    window.ga4Datalayer = window.ga4Datalayer || [];

    if (!window.__gCrWeb) {
      window.__gCrWeb = {};
    }
  }, []);

  useEffect(() => {
    load();
    initCustomerIO();
    const url = parse(window.location.href, true);
    const query = url.query;
    const { utm_source, utm_campaign, utm_medium, utm_content, utm_term } = query;
    if (utm_source) {
      localStorage.setItem('utm_source', utm_source);
    }
    if (utm_campaign) {
      localStorage.setItem('utm_campaign', utm_campaign);
    }
    if (utm_medium) {
      localStorage.setItem('utm_medium', utm_medium);
    }
    if (utm_content) {
      localStorage.setItem('utm_content', utm_content);
    }
    if (utm_term) {
      localStorage.setItem('utm_term', utm_term);
    }

    // track vu
    analytics.trackVu();

    const handlePreloadError = (event: Event) => {
      const pageAlreadyRefreshed = getStorageItem(CHUNK_REFRESH_KEY) === 'true';
      if (!pageAlreadyRefreshed) {
        setStorageItem(CHUNK_REFRESH_KEY, 'true');
        event.preventDefault();
        window.location.reload();
      }
    };

    window.addEventListener('vite:preloadError', handlePreloadError);

    // Clear chunk-refresh once the app has mounted and settled
    const timer = setTimeout(() => {
      removeStorageItem(CHUNK_REFRESH_KEY);
    }, 5000);

    return () => {
      window.removeEventListener('vite:preloadError', handlePreloadError);
      clearTimeout(timer);
    };
  }, []);

  const router = React.useMemo(() => {
    return createBrowserRouter(
      createRoutesFromElements(
        <Route
          path="*"
          ErrorBoundary={RouteErrorBoundary}
          element={
            <>
              <RouteTracker />
              <ChunkErrorBoundary>
                <Routes>
                  <Route path={`/${ERoutePrincipal.DIGIDOM_CGV}`} element={<DigidomCgv />} />
                  <Route
                    path="/auth/login"
                    element={
                      <Suspense fallback={<MainLoadingDigidom />}>
                        <Login />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/auth/logout"
                    element={
                      <Suspense fallback={<MainLoadingDigidom />}>
                        <LogOut />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/auth/forgot-password"
                    element={
                      <Suspense fallback={<MainLoadingDigidom />}>
                        <ForgotPassword />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/validation-gc/:action/:source"
                    element={
                      <Suspense fallback={<MainLoadingDigidom />}>
                        <GoCardLessValidation />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/validation-gc/:action"
                    element={
                      <Suspense fallback={<MainLoadingDigidom />}>
                        <GoCardLessValidation />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/doc/:cryptedLink"
                    element={
                      <Suspense fallback={<MainLoadingDigidom />}>
                        <ViewDocument />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/:orga/*"
                    element={
                      <Suspense fallback={<MainLoadingDigidom />}>
                        <Home />
                      </Suspense>
                    }
                  />
                  <Route path="/" element={<Impersonation />} />
                </Routes>
              </ChunkErrorBoundary>
            </>
          }
        />,
      ),
    );
  }, []);

  return (
    <ApolloProvider client={apolloClient}>
      <ErrorBoundary>
        <ChunkErrorBoundary>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
            <ContextProvider>
              <MuiThemeProvider theme={themeBV}>
                <GlobalSnackBar />
                <RouterProvider router={router} />
              </MuiThemeProvider>
            </ContextProvider>
          </LocalizationProvider>
        </ChunkErrorBoundary>
      </ErrorBoundary>
    </ApolloProvider>
  );
};

export default App;
